import { Box, List, Stack, Strong, Text } from 'braid-design-system';
import type { MDXComponents } from 'mdx/types';
import {
  Children,
  type ComponentProps,
  type ReactNode,
  createContext,
  useContext,
} from 'react';
import { Blockquote, InlineCode, SmartTextLink } from 'scoobie';
import { img } from 'scoobie/styles/img.css';

import { HorizontalRule } from './HorizontalRule';
import { MdxCodeBlock } from './MdxCodeBlock';
import { MdxOrderedList } from './MdxOrderedList';
import { createSpacedHeading } from './SpacedHeading';
import { MdxTable } from './Table';
import { TableCell } from './TableCell';
import type { TableAlign } from './TableContext';
import { BaseTableRow } from './TableRow';
import { Wrapper } from './Wrapper';
import { SIZE_TO_SPACE } from './size';

import * as styles from './mdxComponents.css';

const disableParagraphContext = createContext<boolean>(false);
const preContext = createContext<boolean>(false);

// Don't try to be clever here, this is what you want. No, really. `Text`
// renders inline formatting correctly and fixes the line height. If some
// node is not wrapped in a paragraph and it should be, wrap it using a
// remark plugin, not here.
// TODO: Follow own warning above and consider tree-walking and removing nested paragraphs?
const MdxParagraph = ({ children }: { children?: ReactNode }) =>
  useContext(disableParagraphContext) ? (
    children
  ) : (
    <DisableParagraph>
      <Text>{children}</Text>
    </DisableParagraph>
  );

const DisableParagraph = ({ children }: { children: ReactNode }) => (
  <disableParagraphContext.Provider value={true}>
    {children}
  </disableParagraphContext.Provider>
);

const Code: MDXComponents['code'] = ({
  children = '',
  className,
  // @ts-expect-error Inserted by `rehypeCodeMeta` plugin
  meta,
}) =>
  useContext(preContext) ? (
    <MdxCodeBlock className={className} meta={meta as string | undefined}>
      {children as string}
    </MdxCodeBlock>
  ) : (
    <InlineCode>{children}</InlineCode>
  );

// This is unlikely to look attractive, but avoids some horrible casts
const toBraidChildren = (
  children?: ReactNode,
): ComponentProps<typeof Stack>['children'] => {
  if (
    typeof children === 'object' &&
    children !== null &&
    Symbol.iterator in children
  ) {
    return Children.toArray(children)
      .filter((child) => child !== '\n')
      .map(toBraidChildren);
  }

  if (typeof children === 'string' || typeof children === 'number') {
    return <>{children}</>;
  }

  return children;
};

const space = SIZE_TO_SPACE.standard;

export const mdxComponents: MDXComponents = {
  a: ({ children, href, title }) => (
    <SmartTextLink href={href ?? ''} title={title}>
      {children}
    </SmartTextLink>
  ),
  blockquote: ({ children }) => (
    <Blockquote size="standard">{toBraidChildren(children)}</Blockquote>
  ),
  code: Code,
  h1: createSpacedHeading(1),
  h2: createSpacedHeading(2),
  h3: createSpacedHeading(3),
  h4: createSpacedHeading(4),
  h5: createSpacedHeading(5),
  h6: createSpacedHeading(6),
  hr: HorizontalRule,
  img: ({
    // @ts-expect-error -- This is set to `'default' | 'none'` by the `imageToJsx` plugin.
    'data-scoobie-style': style,
    ...props
  }) => (
    // For wide SVGs like Mermaid diagrams
    // We use a plain `img` tag inside because we need to pass through the raw
    // `width` and `height` attributes.
    <Box className={styles.imgContainer} overflow="auto">
      <img {...props} className={style === 'none' ? undefined : img} />
    </Box>
  ),
  li: ({ children }) => (
    <Stack space={space}>{toBraidChildren(children)}</Stack>
  ),
  ol: (props) => (
    <MdxOrderedList {...props}>
      {toBraidChildren(props.children)}
    </MdxOrderedList>
  ),
  p: MdxParagraph,
  pre: (props) => (
    <preContext.Provider value={true}>{props.children}</preContext.Provider>
  ),
  span: (props) => <Box {...props} component="span" />,
  strong: ({ children }) => <Strong>{children}</Strong>,
  table: ({ children }) => <MdxTable>{children}</MdxTable>,
  td: ({ style, children }) => (
    <TableCell
      align={style?.textAlign as TableAlign | undefined}
      component="td"
    >
      {toBraidChildren(children)}
    </TableCell>
  ),
  th: ({ style, children }) => (
    <TableCell
      align={style?.textAlign as TableAlign | undefined}
      component="th"
    >
      {toBraidChildren(children)}
    </TableCell>
  ),
  tr: ({ children }) => <BaseTableRow>{children}</BaseTableRow>,
  ul: ({ children }) => (
    <List space={space} type="bullet">
      {toBraidChildren(children)}
    </List>
  ),
  MdxFlowElement: DisableParagraph,
  Layout: ({ children }) => <Wrapper>{children}</Wrapper>,
};
